// src/api/useUpdate.js
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import { Grid, Button, CircularProgress } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const useUpdate = (queryKey, token, redirectPath, successMessage, errorMessage, showSuccessToast = true) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      console.log(`Making PUT request to ${process.env.REACT_APP_API_BASE_URL}/${queryKey}/${id}`, {
        hasToken: !!token,
        headers
      });
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/${queryKey}/${id}`,
          data,
          { headers }
        );
        console.log("API response:", response.data);
        return response.data;
      } catch (error) {
        console.error("Update error:", error.response?.data || error.message);
        console.error("Request details:", {
          endpoint: `${process.env.REACT_APP_API_BASE_URL}/${queryKey}/${id}`,
          status: error.response?.status,
          statusText: error.response?.statusText,
          headers: headers,
          errorMessage: error.message
        });
        throw error;
      }
    },
    onSuccess: () => {
      setSuccess(true);
      setLoading(false);
      if (showSuccessToast) {
        toast.success(successMessage || t(`${queryKey}.updateSuccess`, { defaultValue: t("api.updateSuccess") }), {
          position: "top-right",
          autoClose: 3000,
        });
      }
      queryClient.invalidateQueries(queryKey);
      if (redirectPath) {
        navigate(redirectPath);
      }
    },
    onError: (error) => {
      setSuccess(false);
      setLoading(false);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          t(`${queryKey}.updateError`, { defaultValue: t("api.updateError") });
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
      });
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const handleUpdate = (id, data) => {
    console.log(`Updating ${queryKey}/${id} with data:`, data);
    updateMutation.mutate({ id, data });
  };

  const Buttons = ({ onSubmit }) => (
    <Grid container justifyContent={"end"} mt={2}>
      <Button
        variant="contained"
        color="primary"
        disabled={loading}
        sx={{ m: "0em 1em" }}
      >
        {t("api.cancel")}
      </Button>
      <Button
        variant="contained"
        onClick={onSubmit}
        color="primary"
        disabled={loading}
        startIcon={loading && <CircularProgress size={20} />}
      >
        {loading ? t("api.updating") : t("api.update")}
      </Button>
    </Grid>
  );

  return {
    handleUpdate,
    Buttons,
    loading,
    success,
    setSuccess,
  };
};

export default useUpdate;
